import React, { useState, useEffect, useCallback } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { blogAPI } from '../services/api';
import { Calendar, Clock, ArrowRight, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const categories = ['All', 'Treatment', 'Prevention', 'Care', 'Research'];

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const category = selectedCategory === 'All' ? null : selectedCategory;
      const data = await blogAPI.getPosts(page, 9, category);
      setPosts(data.posts);
      setTotalPages(data.totalPages);
      setError(null);
    } catch {
      setError('Failed to load blog posts. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [page, selectedCategory]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category === 'All' ? null : category);
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title="Blog" path="/blog" description="Medical insights, cancer awareness articles, conference updates, and community health news by Dr. Venkata Pradeep Babu Koyyala." keywords="Cancer Blog, Oncology Articles, Cancer Treatment Updates, Dr Koyyala Blog" />
      <Navigation />
      <div className="pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Medical Insights & Updates
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Stay informed about the latest developments in oncology, cancer care, and medical research
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category || (category === 'All' && !selectedCategory) ? 'default' : 'outline'}
                onClick={() => handleCategoryChange(category)}
                className={selectedCategory === category || (category === 'All' && !selectedCategory) ? 'bg-teal-700 hover:bg-teal-800' : ''}
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 text-teal-700 animate-spin" />
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="text-center py-20">
              <p className="text-red-600 text-lg">{error}</p>
              <Button onClick={fetchPosts} className="mt-4 bg-teal-700 hover:bg-teal-800">
                Try Again
              </Button>
            </div>
          )}

          {/* Blog Posts Grid */}
          {!loading && !error && (
            <>
              {posts.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-gray-600 text-lg">No blog posts found.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {posts.map((post) => (
                    <Card key={post.id} className="hover:shadow-lg transition-shadow duration-300 flex flex-col">
                      <CardHeader>
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="secondary" className="bg-teal-100 text-teal-700">
                            {post.category}
                          </Badge>
                        </div>
                        <CardTitle className="text-xl mb-2 line-clamp-2">{post.title}</CardTitle>
                        <CardDescription className="line-clamp-3">{post.excerpt}</CardDescription>
                      </CardHeader>
                      <CardContent className="mt-auto">
                        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(post.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{post.readTime}</span>
                          </div>
                        </div>
                        <Link to={`/blog/${post.slug}`}>
                          <Button variant="ghost" className="w-full justify-between group">
                            Read More
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-12">
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="border-teal-700 text-teal-700 hover:bg-teal-50"
                  >
                    Previous
                  </Button>
                  <span className="text-gray-600">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="border-teal-700 text-teal-700 hover:bg-teal-50"
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BlogPage;